'use client'

import { useState } from 'react'
import { pastDueBannerMessage, type PastDueBannerState } from '@/lib/billing/past-due-banner'

export type PastDueBannerData = {
  daysLeft: number
  graceEndsAtIso: string
  urgent: boolean
}

/**
 * "Your payment didn't go through" — shown site-wide while a customer is inside
 * the dunning grace window.
 *
 * NOT DISMISSIBLE, unlike TrialBanner. A trial notice is informational and
 * resolves itself; this one has a deadline, and missing it means losing access
 * that is currently being held open. The tone stays calm and blames the card
 * rather than the customer: they are a paying subscriber whose renewal failed,
 * most often on a card that simply expired.
 *
 * The word "upgrade" appears nowhere. Being sold something you already bought
 * is the exact insult this whole change exists to remove.
 */
export default function PastDueBanner({ data }: { data: PastDueBannerData | null }) {
  const [busy, setBusy] = useState(false)
  if (!data) return null

  const state: PastDueBannerState = {
    daysLeft: data.daysLeft,
    graceEndsAt: new Date(data.graceEndsAtIso),
    urgent: data.urgent,
  }

  async function openPortal() {
    setBusy(true)
    try {
      // The same call the Settings and Pricing "Manage billing" buttons make.
      const r = await fetch('/api/stripe/portal', { method: 'POST' })
      const j = (await r.json()) as { url?: string }
      if (j.url) window.location.href = j.url
      else setBusy(false)
    } catch {
      setBusy(false)
    }
  }

  return (
    <div
      role="status"
      className={
        data.urgent
          ? 'w-full border-b border-red-300 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-100'
          : 'w-full border-b border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100'
      }
    >
      <div className="mx-auto flex w-full max-w-[1100px] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 text-sm">
        <span className="font-medium">{pastDueBannerMessage(state)}</span>
        <span className="opacity-80">Update your card to keep it.</span>
        <button
          type="button"
          onClick={openPortal}
          disabled={busy}
          className="ml-auto rounded-md bg-foreground px-3 py-1 text-xs font-semibold text-background disabled:opacity-60"
        >
          {busy ? 'Opening…' : 'Update payment method'}
        </button>
      </div>
    </div>
  )
}
